import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { CenterToWindow, centerToWindowPropTypes } from "../CenterToWindow";
import { FocusContainer } from "../FocusContainer";
import { Shadow } from "../Shadow";
import { Transition } from "../Transition";
import { Width, widthPropTypes } from "../Width";
import styles from "./Overlay.module.scss";

const DURATION_FAST = 0.15;
const DURATION_NORMAL = 0.25;
const OPAQUE = 1;
const ROTATION_MAXIMUM = 20;
const ROTATION_MEDIUM = 12;
const ROTATION_NONE = 0;
const TRANSPARENT = 0;
const FILTER_MAXIMUM = "2px";
const FILTER_NONE = "0px";

/**
 * Overlay provide a way to display an element centered (or full screen) and appear as a top level
 * "overlay".
 */
export const Overlay = (props) => {
  const { children, location = "hidden", width } = props;
  const [currentLocation, setCurrentLocation] = React.useState("hidden");

  return (
    <CenterToWindow location={location} overlayClassName={styles.container}>
      <Transition {...getTransitionProps()}>
        <Shadow>
          <Width width={width}>
            <FocusContainer {...getFocusContainerProps()}>{renderContent()}</FocusContainer>
          </Width>
        </Shadow>
      </Transition>
    </CenterToWindow>
  );

  function renderContent() {
    return <div {...getFlyoutContentProps()}>{children}</div>;
  }

  function getTransitionProps() {
    return {
      animate: getAnimationValues(),
      className: getAnimationClass(),
      onTransformationEnd: handleTransformationEnd,
      transition: getTransition(),
    };
  }

  function getFocusContainerProps() {
    return {
      "aria-hidden": props["aria-hidden"] || currentLocation === "hidden",
      revealed: currentLocation !== "hidden",
    };
  }

  function getFlyoutContentProps() {
    return {
      "aria-describedby": props["aria-describedby"],
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      ...(props["aria-label"] ||
        (props["aria-labelledby"] && {
          "aria-modal": "true",
          role: "dialog",
        })),
    };
  }

  function getAnimationClass() {
    return classNames(styles.transition, {
      [styles.fullScreen]: currentLocation === "full-screen" || location === "full-screen",
    });
  }

  function handleTransformationEnd() {
    setCurrentLocation(location);
  }

  function getAnimationValues() {
    return {
      filter: getFilter(),
      opacity: getOpacity(),
      rotateX: getXRotation(),
    };
  }

  function getFilter() {
    return location === "hidden"
      ? [`blur(${FILTER_NONE})`, `blur(${FILTER_MAXIMUM})`]
      : [`blur(${FILTER_MAXIMUM})`, `blur(${FILTER_NONE})`];
  }

  function getOpacity() {
    return location === "hidden" ? [OPAQUE, TRANSPARENT] : [TRANSPARENT, OPAQUE];
  }

  function getXRotation() {
    return location === "hidden"
      ? [ROTATION_NONE, ROTATION_NONE]
      : location === "center"
      ? [ROTATION_MAXIMUM, ROTATION_NONE]
      : [ROTATION_MEDIUM, ROTATION_NONE];
  }

  function getTransition() {
    return { duration: location === "hidden" ? DURATION_FAST : DURATION_NORMAL, ease: "easeOut" };
  }
};

Overlay.propTypes = {
  /** aria-describedby text to provide accessibility description for the flyout. */
  "aria-describedby": PropTypes.string,

  /** aria-label text to provide accessibility description for the flyout. */
  "aria-label": PropTypes.string,

  /** aria-labelledby text to provide accessibility label for the flyout. */
  "aria-labelledby": PropTypes.string,

  /** Content that will be rendered. */
  children: PropTypes.node,

  /** Determines where overlay will be rendered, or whether it's hidden. */
  location: centerToWindowPropTypes.location,

  /** Width of content container. Should not be given for full-screen location. */
  width: widthPropTypes.width,
};
